import React, { useMemo } from 'react';
import { CheckSquare, FileImage, Loader2, Square } from 'lucide-react';
import { groupDrawingsByType } from '../../utils/accommodationDrawingUtils';

const BoqAccommodationDrawingsPicker = ({
  drawings = [],
  isLoading = false,
  isError = false,
  useAllDrawings = false,
  onUseAllDrawingsChange,
  selectedFileKeys = [],
  onToggleFileKey,
  onSelectAll,
  onClearSelection,
  disabled = false
}) => {
  const groupedDrawings = useMemo(() => groupDrawingsByType(drawings), [drawings]);
  const selectableDrawings = useMemo(
    () => drawings.filter((drawing) => Boolean(drawing.fileKey)),
    [drawings]
  );
  const selectedCount = useAllDrawings ? selectableDrawings.length : selectedFileKeys.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-[#E5E7EB] bg-[#FAFBFC] px-4 py-10 text-sm text-[#6B7280]">
        <Loader2 className="h-4 w-4 animate-spin text-[#2A6A73]" />
        Loading stored drawings...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-600">
        Could not load stored drawings. You can still add PDF files in the section below.
      </div>
    );
  }

  if (!drawings.length) {
    return (
      <div className="rounded-lg border border-dashed border-[#D1D5DB] bg-[#FAFBFC] px-4 py-8 text-center">
        <FileImage className="mx-auto h-8 w-8 text-[#94A3B8]" />
        <p className="mt-2 text-sm font-medium text-[#374151]">No stored drawings yet</p>
        <p className="mt-1 text-xs text-[#6B7280]">
          Upload drawings from the accommodation page, or add PDFs below.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[#111827]">Stored drawings</span>
          <span className="rounded-full bg-[#E8F3F5] px-2 py-0.5 text-[11px] font-semibold text-[#2A6A73]">
            {drawings.length}
          </span>
          {selectedCount > 0 ? (
            <span className="text-xs text-[#6B7280]">
              · {selectedCount} selected for analysis
            </span>
          ) : null}
        </div>
        {!useAllDrawings && selectableDrawings.length > 1 ? (
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              disabled={disabled}
              onClick={onSelectAll}
              className="font-medium text-[#2A6A73] hover:underline disabled:opacity-50">
              Select all
            </button>
            <span className="text-[#D1D5DB]">|</span>
            <button
              type="button"
              disabled={disabled || !selectedFileKeys.length}
              onClick={onClearSelection}
              className="font-medium text-[#6B7280] hover:underline disabled:opacity-50">
              Clear
            </button>
          </div>
        ) : null}
      </div>

      <button
        type="button"
        disabled={disabled || !selectableDrawings.length}
        onClick={() => onUseAllDrawingsChange?.(!useAllDrawings)}
        className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
          useAllDrawings
            ? 'border-[#2A6A73] bg-[#E8F3F5]'
            : 'border-[#E5E7EB] bg-white hover:border-[#CBD5E1] hover:bg-[#FAFBFC]'
        } disabled:cursor-not-allowed disabled:opacity-50`}>
        {useAllDrawings ? (
          <CheckSquare className="h-5 w-5 shrink-0 text-[#2A6A73]" />
        ) : (
          <Square className="h-5 w-5 shrink-0 text-[#94A3B8]" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#111827]">Use all stored drawings</p>
          <p className="text-xs text-[#6B7280]">
            Include every file uploaded for this accommodation
          </p>
        </div>
      </button>

      {!useAllDrawings ? (
        <div className="max-h-[220px] space-y-3 overflow-y-auto rounded-lg border border-[#E5E7EB] bg-[#FAFBFC] p-2 custom-scrollbar">
          {groupedDrawings.map((group) => (
            <div key={group.type}>
              {groupedDrawings.length > 1 ? (
                <p className="mb-1.5 px-1 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                  {group.type}
                </p>
              ) : null}
              <div className="space-y-1">
                {group.items.map((drawing) => {
                  const key = drawing.fileKey;
                  const isSelectable = Boolean(key);
                  const isChecked = selectedFileKeys.includes(key);
                  const rowKey = `${drawing.id}-${key || drawing.name}`;

                  return (
                    <label
                      key={rowKey}
                      className={`flex items-center gap-2.5 rounded-md border px-2.5 py-2 ${
                        isChecked
                          ? 'border-[#BFD4D8] bg-white shadow-sm'
                          : 'border-transparent bg-transparent hover:bg-white'
                      } ${!isSelectable || disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={disabled || !isSelectable}
                        onChange={() => isSelectable && onToggleFileKey?.(key)}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#2A6A73] focus:ring-[#2A6A73]"
                      />
                      <FileImage className="h-4 w-4 shrink-0 text-[#2A6A73]" />
                      <span
                        className="min-w-0 flex-1 truncate text-sm text-[#111827]"
                        title={drawing.name}>
                        {drawing.name}
                      </span>
                      {groupedDrawings.length === 1 && drawing.drawingType ? (
                        <span className="shrink-0 rounded bg-[#F1F5F9] px-1.5 py-0.5 text-[10px] font-medium text-[#64748B]">
                          {drawing.drawingType}
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-lg bg-[#F4F9FA] px-3 py-2 text-xs text-[#2A6A73]">
          All {selectableDrawings.length} stored drawing
          {selectableDrawings.length === 1 ? '' : 's'} will be used for extraction.
        </p>
      )}
    </div>
  );
};

export default BoqAccommodationDrawingsPicker;
